import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { LocationSublocationDirective } from '@Shared/Directives/proprietary/location-sublocation.directive';
import { ContractObjectService } from '@Core/models/proprietary/contract/contract-object.service';
import { LocationState } from '@Core/models/proprietary/contract/location.state';

@Component({
    standalone: false,
    selector: 'ep-location-selector',
    templateUrl: './location-selector.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LocationSelectorComponent),
            multi: true
        }
    ]
})
export class LocationSelectorComponent implements ControlValueAccessor, OnDestroy {
    @Output()
    blur = new EventEmitter();

    @Output()
    focus = new EventEmitter();

    @Input('disabled')
    controlDisabled: boolean = false; //is this the right way?

    @Input('userDefinedMode')
    set userDefinedMode(value: boolean) {
        if (value === true) {
            this._userDefinedMode = true;
        }
    }

    get userDefinedMode(): boolean {
        return this._userDefinedMode;
    }

    private _userDefinedMode: boolean = false;

    innerValue: any = '';

    @Select(LocationState.locationCodeTable)
    locationCodeTable$: Observable<any[]>;

    @Select(LocationState.locationIDCodeTable)
    locationIDCodeTable$: Observable<any[]>;

    @Select(LocationState.currentLocation)
    location$: Observable<any>;

    location: any = {};
    locationSub: Subscription;

    constructor(
        private store: Store,
        private cdRef: ChangeDetectorRef,
        private contractOS: ContractObjectService,
        public locationSublocationDirective: LocationSublocationDirective
    ) { }

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            if (this.userDefinedMode) {
                this.setLocID(v);
            } else {
                this.setLocNum(v);
            }
            this.onChangeCallback(v); //NOTE:   The order of these callbacks matters.  Change before Touched.
            this.onTouchedCallback();
        }
    }

    onFocus() {
        this.focus.emit();
    }

    onBlur() {
        this.blur.emit();
        this.onTouchedCallback();
    }

    //From ControlValueAccessor interface - Allows Angular to update the model
    writeValue(value: string): void {
        this.innerValue = value;
        if (value !== undefined) {
            if (this.userDefinedMode) {
                this.setLocID(value);
            } else {
                this.setLocNum(value);
                this.setCurrentLocation(value);
            }
        }
        this.cdRef.detectChanges();
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    ngOnDestroy() {
        if (this.locationSub) {
            this.locationSub.unsubscribe(); //IS THIS NEEDED?
        }
        if (this.location$) {
            delete this.location$;
        }
    }

    setCurrentLocation(value: any) {
        if (value !== undefined) {
            this.contractOS.setCurrentParentLocation(value);
        }
    }

    setLocNum(value: any) {
        this.locationSublocationDirective.updateLocNum(value);
    }

    setLocID(value: any) {
        this.locationSublocationDirective.updateLocID(value);
    }

    modalEdit() {
        if (!this.userDefinedMode) {
            this.contractOS.modalLocationEdit({ Loc_Num: this.value });
            if (this.locationSub) {
                this.locationSub.unsubscribe();
            }
            //when location is added it will become the current location, if deleted the current loc is cleared (trigger the popup to close)
            this.locationSub = this.location$.subscribe((x) => {
                this.value = x.Loc_Num;
                if (!x.Loc_ID) {
                    this.location.unsubscribe();
                }
            });
        }
    }
}
